import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useConfirm } from "@/context/ConfirmContext";
import { toast } from "sonner";
import {
  Plus, Edit3, Trash2, X, ExternalLink, Calendar, DollarSign,
  Building2, Search, TrendingUp, Target, CheckCircle2, AlertTriangle, Radio,
} from "lucide-react";

const STATUS_META = {
  watchlist:    { label: "Watchlist",    color: "#B0BEC5" },
  drafting:     { label: "Drafting",     color: "#89b4fa" },
  submitted:    { label: "Submitted",    color: "#a6e3a1" },
  under_review: { label: "Under review", color: "#d4b876" },
  awarded:      { label: "Awarded",      color: "#a6e3a1" },
  declined:     { label: "Declined",     color: "#ef5b6b" },
  withdrawn:    { label: "Withdrawn",    color: "#8C8C8C" },
};
const STATUS_ORDER = ["watchlist","drafting","submitted","under_review","awarded","declined","withdrawn"];

const IMPACT_META = {
  low:              { label: "Low",              color: "#8C8C8C" },
  medium:           { label: "Medium",           color: "#B0BEC5" },
  high:             { label: "High",             color: "#89b4fa" },
  transformational: { label: "Transformational", color: "#a6e3a1" },
};

const EDIT_ROLES = new Set(["chief", "deputy_chief", "head_councillor", "councillor", "executive_director", "band_registry_clerk"]);

const currency = (n) => n == null ? "—" : "$" + Math.round(n).toLocaleString();
const currencyShort = (n) => {
  if (n == null) return "—";
  if (n >= 1_000_000) return `$${(n/1_000_000).toFixed(1)}M`;
  if (n >= 1_000) return `$${(n/1_000).toFixed(0)}K`;
  return `$${Math.round(n)}`;
};

function daysColor(days) {
  if (days == null) return "#8C8C8C";
  if (days < 0) return "#ef5b6b";
  if (days <= 14) return "#d4b876";
  if (days <= 45) return "#B0BEC5";
  return "#a6e3a1";
}
function daysLabel(days) {
  if (days == null) return "no deadline";
  if (days < 0) return `${Math.abs(days)}d past`;
  if (days === 0) return "due today";
  return `${days}d left`;
}

export default function Funding() {
  const { user } = useAuth();
  const confirm = useConfirm();
  const canEdit = user && (user.is_super_admin || EDIT_ROLES.has(user.role));

  const [items, setItems] = useState([]);
  const [summary, setSummary] = useState(null);
  const [departments, setDepartments] = useState([]);
  const [dept, setDept] = useState("");
  const [status, setStatus] = useState("");
  const [funderQ, setFunderQ] = useState("");
  const [hidePast, setHidePast] = useState(false);
  const [editing, setEditing] = useState(null); // opportunity or "new"

  const load = async () => {
    const params = new URLSearchParams();
    if (dept) params.set("department", dept);
    if (status) params.set("status", status);
    if (funderQ) params.set("funder", funderQ);
    if (hidePast) params.set("hide_past_deadline", "true");
    const [it, sum] = await Promise.all([
      api.get(`/funding${params.toString() ? "?" + params.toString() : ""}`),
      api.get("/funding/summary"),
    ]);
    setItems(it.data);
    setSummary(sum.data);
  };
  useEffect(() => {
    api.get("/funding/departments").then(r => setDepartments(r.data.departments)).catch(() => {});
  }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [dept, status, funderQ, hidePast]);

  const remove = async (item) => {
    const ok = await confirm({
      title: "Remove funding opportunity?",
      body: `"${item.title}" will be permanently removed from the pipeline. This does not affect an already-signed application.`,
      confirmLabel: "Remove", tone: "danger",
    });
    if (!ok) return;
    try { await api.delete(`/funding/${item.id}`); toast.success("Opportunity removed"); load(); }
    catch (e) { toast.error(e.response?.data?.detail || "Failed to remove"); }
  };

  const kpi = useMemo(() => {
    if (!summary) return null;
    const c = summary.counts;
    return [
      { label: "Watchlist",    value: c.watchlist,    icon: Target,        color: "#B0BEC5" },
      { label: "Drafting",     value: c.drafting,     icon: Edit3,         color: "#89b4fa" },
      { label: "In review",    value: c.submitted + c.under_review, icon: Radio, color: "#d4b876" },
      { label: "Awarded YTD",  value: currencyShort(summary.awarded_ytd_value), icon: CheckCircle2, color: "#a6e3a1", sub: `${c.awarded} project${c.awarded===1?"":"s"}` },
    ];
  }, [summary]);

  return (
    <div className="max-w-[1400px] mx-auto px-6 py-10">
      {/* Page header */}
      <div className="flex items-baseline justify-between gap-4 flex-wrap mb-8">
        <div>
          <div className="label-mono" style={{ color: "#89b4fa" }}>::: Funding pipeline · Live</div>
          <h1 className="font-display text-4xl mt-2">Grants &amp; Calls for Proposals</h1>
          <p className="text-white/60 text-sm mt-2 max-w-2xl">
            Every active funding opportunity the Nation is tracking — federal, provincial, foundation, and Indigenous streams.
            Curated by leadership. Deep-linked to source documents, tagged by department and expected impact.
          </p>
        </div>
        {canEdit && (
          <button onClick={() => setEditing("new")} className="btn-cta primary" data-testid="new-funding-btn">
            <Plus size={12}/> New opportunity
          </button>
        )}
      </div>

      {/* KPI strip */}
      {kpi && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-8" data-testid="funding-kpi-strip">
          {kpi.map(k => (
            <div key={k.label} className="panel p-5 corner-tick">
              <div className="label-mono flex items-center gap-2" style={{ color: k.color }}>
                <k.icon size={12}/> {k.label}
              </div>
              <div className="font-display text-3xl mt-3 tabular-nums" style={{ color: k.color }}>{k.value}</div>
              {k.sub && <div className="font-mono text-[10px] text-white/50 mt-1">{k.sub}</div>}
            </div>
          ))}
        </div>
      )}

      {/* Pipeline value banner */}
      {summary && summary.pipeline_value > 0 && (
        <div className="border-l-2 pl-4 mb-8 flex items-baseline gap-4 flex-wrap" style={{ borderColor: "#89b4fa" }}>
          <span className="label-mono" style={{ color: "#89b4fa" }}>Live pipeline value</span>
          <span className="font-display text-2xl tabular-nums">{currency(summary.pipeline_value)}</span>
          <span className="font-mono text-xs text-white/40">across {(summary.counts.total - summary.counts.awarded - summary.counts.declined - summary.counts.withdrawn) || 0} active opportunities</span>
        </div>
      )}

      {/* Filters */}
      <div className="panel p-4 mb-6 flex items-center gap-3 flex-wrap" data-testid="funding-filters">
        <div className="flex items-center gap-2">
          <Building2 size={12} className="text-white/40"/>
          <select value={dept} onChange={e => setDept(e.target.value)} className="bg-transparent border border-white/15 px-2 py-1.5 font-mono text-xs" data-testid="filter-department">
            <option value="">All departments</option>
            {departments.map(d => <option key={d} value={d}>{d}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2">
          <TrendingUp size={12} className="text-white/40"/>
          <select value={status} onChange={e => setStatus(e.target.value)} className="bg-transparent border border-white/15 px-2 py-1.5 font-mono text-xs" data-testid="filter-status">
            <option value="">All statuses</option>
            {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
          </select>
        </div>
        <div className="flex items-center gap-2 flex-1 min-w-[200px]">
          <Search size={12} className="text-white/40"/>
          <input value={funderQ} onChange={e => setFunderQ(e.target.value)} placeholder="Search funder…" className="w-full bg-transparent border border-white/15 px-2 py-1.5 font-mono text-xs" data-testid="filter-funder"/>
        </div>
        <label className="flex items-center gap-2 font-mono text-xs text-white/60 cursor-pointer">
          <input type="checkbox" checked={hidePast} onChange={e => setHidePast(e.target.checked)} data-testid="filter-hide-past"/>
          Hide past-deadline
        </label>
      </div>

      {/* Board */}
      {items.length === 0 ? (
        <EmptyState canEdit={canEdit} onNew={() => setEditing("new")} />
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-5" data-testid="funding-board">
          {items.map(op => (
            <OpportunityCard key={op.id} op={op} canEdit={canEdit} onEdit={() => setEditing(op)} onRemove={() => remove(op)} />
          ))}
        </div>
      )}

      {editing && (
        <FundingEditor
          data={editing === "new" ? null : editing}
          departments={departments}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); load(); }}
        />
      )}
    </div>
  );
}

/* ---------- Opportunity card ---------- */

function OpportunityCard({ op, canEdit, onEdit, onRemove }) {
  const sMeta = STATUS_META[op.status] || STATUS_META.watchlist;
  const fMeta = IMPACT_META[op.financial_impact] || IMPACT_META.medium;
  const sMeta2 = IMPACT_META[op.socio_economic_impact] || IMPACT_META.medium;

  return (
    <div className="panel panel-lift p-5 corner-tick relative" data-testid={`funding-card-${op.id}`}>
      {/* Row 1: status + deadline */}
      <div className="flex items-center justify-between">
        <span className="tag" style={{ color: sMeta.color, borderColor: sMeta.color + "55" }}>
          {sMeta.label}
        </span>
        <div className="font-mono text-[10px] flex items-center gap-1" style={{ color: daysColor(op.days_to_deadline) }}>
          <Calendar size={11}/> {daysLabel(op.days_to_deadline)}
        </div>
      </div>

      {/* Title */}
      <h3 className="font-display text-lg mt-3 leading-tight">{op.title}</h3>
      <div className="font-mono text-xs text-white/60 mt-1">
        {op.funder}{op.program_stream && <> · <span className="text-white/50">{op.program_stream}</span></>}
      </div>

      {/* Amount */}
      <div className="mt-4 flex items-baseline gap-2">
        <DollarSign size={14} className="text-white/40"/>
        <span className="font-display text-xl tabular-nums">
          {op.amount_min && op.amount_max ? `${currencyShort(op.amount_min)} – ${currencyShort(op.amount_max)}`
            : op.amount_max ? `up to ${currencyShort(op.amount_max)}`
            : op.amount_min ? `from ${currencyShort(op.amount_min)}`
            : "—"}
        </span>
      </div>

      {/* Department + impact badges */}
      <div className="mt-4 flex flex-wrap gap-1.5">
        <span className="tag" style={{ color: "#B0BEC5", borderColor: "rgba(176,190,197,0.4)" }}>{op.department}</span>
        <span className="tag" style={{ color: fMeta.color, borderColor: fMeta.color + "55" }} title="Financial impact">
          $ {fMeta.label}
        </span>
        <span className="tag" style={{ color: sMeta2.color, borderColor: sMeta2.color + "55" }} title="Socio-economic impact">
          People {sMeta2.label}
        </span>
      </div>

      {/* Description */}
      {op.description && (
        <p className="text-white/70 text-sm mt-3 line-clamp-3">{op.description}</p>
      )}

      {/* Footer: lead + link + edit/delete */}
      <div className="mt-4 pt-3 border-t border-white/10 flex items-center justify-between gap-2">
        <div className="font-mono text-[10px] text-white/50 truncate">
          {op.assigned_lead ? `Lead: ${op.assigned_lead.name}` : "unassigned"}
        </div>
        <div className="flex items-center gap-1">
          {op.link && (
            <a href={op.link} target="_blank" rel="noreferrer" className="text-white/60 hover:text-white p-1" title="Open source">
              <ExternalLink size={12}/>
            </a>
          )}
          {canEdit && (
            <>
              <button onClick={onEdit} className="text-white/60 hover:text-white p-1" data-testid={`edit-funding-${op.id}`} title="Edit"><Edit3 size={12}/></button>
              <button onClick={onRemove} className="text-white/60 hover:text-[#ef5b6b] p-1" data-testid={`delete-funding-${op.id}`} title="Remove"><Trash2 size={12}/></button>
            </>
          )}
        </div>
      </div>
    </div>
  );
}

/* ---------- Empty state ---------- */
function EmptyState({ canEdit, onNew }) {
  return (
    <div className="panel p-12 text-center corner-tick">
      <div className="font-mono text-xs text-white/40 label-mono mb-3">::: Pipeline is empty</div>
      <div className="font-display text-xl mb-2">No funding opportunities tracked yet</div>
      <p className="text-white/60 text-sm max-w-md mx-auto">
        Once leadership starts logging calls for proposals, grants, and funder outreach here, this board becomes your Nation's live funding intelligence surface.
      </p>
      {canEdit && (
        <button onClick={onNew} className="btn-cta primary mt-6" data-testid="empty-new-funding">
          <Plus size={12}/> Add first opportunity
        </button>
      )}
    </div>
  );
}

/* ---------- Editor modal ---------- */

function FundingEditor({ data, departments, onClose, onSaved }) {
  const isEdit = !!data;
  const [f, setF] = useState(data ? {
    ...data,
    // Convert ISO to yyyy-mm-dd for the date input
    deadline: data.deadline ? data.deadline.substring(0, 10) : "",
  } : {
    title: "", funder: "", program_stream: "", department: "General",
    amount_min: "", amount_max: "", deadline: "",
    status: "watchlist", link: "", description: "", notes: "",
    financial_impact: "medium", socio_economic_impact: "medium",
  });
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState("");

  const save = async (e) => {
    e.preventDefault();
    setSaving(true); setErr("");
    try {
      const payload = {
        title: f.title, funder: f.funder,
        program_stream: f.program_stream || null,
        department: f.department,
        amount_min: f.amount_min === "" || f.amount_min == null ? null : Number(f.amount_min),
        amount_max: f.amount_max === "" || f.amount_max == null ? null : Number(f.amount_max),
        deadline: f.deadline ? new Date(f.deadline + "T23:59:59Z").toISOString() : null,
        status: f.status,
        link: f.link || null,
        description: f.description || null,
        notes: f.notes || null,
        financial_impact: f.financial_impact,
        socio_economic_impact: f.socio_economic_impact,
      };
      if (isEdit) await api.patch(`/funding/${data.id}`, payload);
      else await api.post("/funding", payload);
      toast.success(isEdit ? "Opportunity updated" : "Opportunity added to pipeline");
      onSaved();
    } catch (e) {
      const msg = e.response?.data?.detail || "Save failed";
      setErr(typeof msg === "string" ? msg : JSON.stringify(msg));
      toast.error("Save failed");
    } finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/90 overflow-y-auto p-4" onClick={onClose}>
      <form onClick={e => e.stopPropagation()} onSubmit={save} className="max-w-3xl mx-auto border border-white/20 bg-[#181825] p-6 my-8 space-y-5" data-testid="funding-editor">
        <div className="flex justify-between items-baseline">
          <div className="label-mono" style={{ color: "#d4b876" }}>::: {isEdit ? "EDIT" : "NEW"} FUNDING OPPORTUNITY</div>
          <button type="button" onClick={onClose} className="text-white/50 hover:text-white" data-testid="close-editor"><X size={20}/></button>
        </div>

        {err && <div className="border border-[#ef5b6b] text-[#ef5b6b] p-3 font-mono text-xs">{err}</div>}

        <input required placeholder="Title (e.g. CMHC Housing Renewal Grant 2026)" value={f.title} onChange={e => setF({...f, title: e.target.value})} className="w-full px-3 py-2.5 font-mono text-sm" data-testid="funding-title"/>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
          <input required placeholder="Funder (e.g. CMHC, ISC, NAN)" value={f.funder} onChange={e => setF({...f, funder: e.target.value})} className="w-full px-3 py-2.5 font-mono text-sm" data-testid="funding-funder"/>
          <input placeholder="Program stream (optional)" value={f.program_stream || ""} onChange={e => setF({...f, program_stream: e.target.value})} className="w-full px-3 py-2.5 font-mono text-sm" data-testid="funding-stream"/>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <label className="label-mono block mb-1">Department</label>
            <select value={f.department} onChange={e => setF({...f, department: e.target.value})} className="w-full px-3 py-2 font-mono text-sm bg-transparent border border-white/20" data-testid="funding-department">
              {departments.map(d => <option key={d} value={d}>{d}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono block mb-1">Status</label>
            <select value={f.status} onChange={e => setF({...f, status: e.target.value})} className="w-full px-3 py-2 font-mono text-sm bg-transparent border border-white/20" data-testid="funding-status">
              {STATUS_ORDER.map(s => <option key={s} value={s}>{STATUS_META[s].label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono block mb-1">Deadline</label>
            <input type="date" value={f.deadline || ""} onChange={e => setF({...f, deadline: e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="funding-deadline"/>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-mono block mb-1">Amount min ($)</label>
            <input type="number" min="0" step="1000" value={f.amount_min ?? ""} onChange={e => setF({...f, amount_min: e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="funding-amount-min"/>
          </div>
          <div>
            <label className="label-mono block mb-1">Amount max ($)</label>
            <input type="number" min="0" step="1000" value={f.amount_max ?? ""} onChange={e => setF({...f, amount_max: e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="funding-amount-max"/>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-3">
          <div>
            <label className="label-mono block mb-1">Financial impact</label>
            <select value={f.financial_impact} onChange={e => setF({...f, financial_impact: e.target.value})} className="w-full px-3 py-2 font-mono text-sm bg-transparent border border-white/20" data-testid="funding-fin-impact">
              {Object.entries(IMPACT_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
          <div>
            <label className="label-mono block mb-1">Socio-economic impact</label>
            <select value={f.socio_economic_impact} onChange={e => setF({...f, socio_economic_impact: e.target.value})} className="w-full px-3 py-2 font-mono text-sm bg-transparent border border-white/20" data-testid="funding-soc-impact">
              {Object.entries(IMPACT_META).map(([k,v]) => <option key={k} value={k}>{v.label}</option>)}
            </select>
          </div>
        </div>

        <input type="url" placeholder="Link to funder page / call-for-proposals" value={f.link || ""} onChange={e => setF({...f, link: e.target.value})} className="w-full px-3 py-2.5 font-mono text-sm" data-testid="funding-link"/>

        <textarea rows={3} placeholder="Description — who is eligible, what activities are funded, key requirements" value={f.description || ""} onChange={e => setF({...f, description: e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="funding-description"/>

        <textarea rows={2} placeholder="Internal notes (visible only to leadership)" value={f.notes || ""} onChange={e => setF({...f, notes: e.target.value})} className="w-full px-3 py-2 font-mono text-sm" data-testid="funding-notes"/>

        <div className="pt-3 flex justify-end gap-3">
          <button type="button" onClick={onClose} className="btn-cta">Cancel</button>
          <button type="submit" disabled={saving} className="btn-cta primary" data-testid="save-funding">{saving ? "Saving…" : (isEdit ? "Update" : "Add to pipeline")}</button>
        </div>
      </form>
    </div>
  );
}
